import axios from 'axios';
import { fetchSeller } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { PROFILE_FETCH_COMMON_INCLUDES } from 'Douleutaras/constants/pro-profile';
import { getCategoryForm } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { decideNextQuestion, filterCallOutFee } from 'Douleutaras/views/job/post-job/hooks/questions-step';
import { CategoryAttribute } from 'Douleutaras/redux/data/v2/categoryForm/types';
import { getMe } from 'Douleutaras/redux/data/users/meSlice';
import { Gis } from 'Douleutaras/components/Forms/gis.class';

import { PostJobSteps, StepStatus } from '../hooks/post-job';

interface GeoCheckCategoryResponse {
  county_slug: string;
  is_valid: boolean;
  message: string;
  municipality_slug: string;
}

export const setTitleEffect = (getCurrentTitle, setCurrentTitle, setCurrentNote) => {
  const { title, note } = getCurrentTitle();

  setCurrentTitle(title);
  setCurrentNote(note);
};

const fetchOriginalFlowEffect = (goToStep, postJobSettings, setPrevStep, jumpTo: PostJobSteps, isValid) => {
  const STEP_ORDER = [
    PostJobSteps.QUESTIONS_STEP,
    PostJobSteps.LOCATION_STEP,
    PostJobSteps.USER_STEP,
    PostJobSteps.DIAGNOSIS_STEP
  ];

  // Based on jumpTo value, it will navigate to currentStep and set history using step order so user can go back
  if (Object.values(PostJobSteps)
        .includes(jumpTo)) {

    // Will set the history based on order
    setPrevStep(STEP_ORDER.slice(0, STEP_ORDER.indexOf(jumpTo)));
    goToStep(jumpTo);

    return;
  }

  goToStep(PostJobSteps.QUESTIONS_STEP);
};

export const fetchDataEffect = (
  proId,
  profile,
  dispatch,
  isValid,
  jumpTo,
  postJobSettings,
  proUsername,
  searchParams,
  setPrevStep,
  setValue,
  user,
  formValues,
  goToStep
) => {
  // Fetch the fresh user data if he is logged
  if (Boolean(user?.id)) {
    dispatch(getMe());
  }

  if (!Boolean(proId)) {
    fetchOriginalFlowEffect(goToStep, postJobSettings, setPrevStep, jumpTo, isValid);

    return;
  }

  if (postJobSettings.CAN_JUMP_TO_STEP) {
    const categoryFromUrl = searchParams.get('category');
    setValue('categoryId', categoryFromUrl);
    goToSingleBidFlow(setPrevStep, goToStep, formValues);

    return;
  }

  fetchSellerEffect(profile,
    dispatch,
    isValid,
    proUsername,
    postJobSettings,
    proId,
    searchParams,
    setPrevStep,
    setValue,
    formValues,
    goToStep);
};

export const initializeCategoryFormId = async (setValue, categoryForm, dispatch, categoryId) => {
  if (Boolean(categoryForm?.id)) {
    setValue('categoryFormId', categoryForm.id);
  } else if (Boolean(categoryId)) {
    const categoryFormResponse = await dispatch(getCategoryForm({ categoryId, seller: false }))
      .unwrap();

    setValue('categoryFormId', categoryFormResponse?.result.id);
  }
};

export const nextButtonEffect = (
    setShowNextButton,
    currentStep,
    setStepStatus,
    hasCallOutFeeStep,
    user,
    postJobSettings
  ) => {
  switch (true) {
    default:
      setShowNextButton(false);
      break;
    case currentStep === PostJobSteps.USER_STEP:
      setStepStatus(hasCallOutFeeStep ? StepStatus.HAS_NEXT : StepStatus.IS_FINAL);
      setShowNextButton(true);
      break;
    case currentStep === PostJobSteps.LOCATION_STEP:
      setStepStatus(
        (!user?.id || postJobSettings.EXISTING_USER_CONTACT_DETAILS_STEP)
        || hasCallOutFeeStep
          ? StepStatus.HAS_NEXT
          : StepStatus.IS_FINAL);
      setShowNextButton(true);
      break;
    case currentStep === PostJobSteps.QUESTIONS_STEP:
      break;
    case currentStep === PostJobSteps.CATEGORY_STEP:
      setShowNextButton(false);
      break;
    case currentStep === PostJobSteps.DIAGNOSIS_STEP:
      setStepStatus(StepStatus.IS_FINAL);
      setShowNextButton(true);
      break;
  }
};

export const setCallOutFeeEffect = (
    categoryForm,
    setHasCallOutFeeStep,
    watch
) => {
  if (categoryForm?.attributes.length > 0 && typeof setHasCallOutFeeStep === 'function') {
    const callOutFeeQuestions = categoryForm.attributes.filter(filterCallOutFee({ include: true }));
    // will try to find valid call out fee
    const callOutFeeQuestion = callOutFeeQuestions.find((callOutQuestion: CategoryAttribute) => {
      return decideNextQuestion({ userSelectedQuestions: watch().questions, question: callOutQuestion });
    });

    setHasCallOutFeeStep(callOutFeeQuestion !== undefined);
  }
};

export const saveToLocalStorageEffect = (setIsFormValid,
                                         isSubmitted,
                                         isValid,
                                         allChoices,
                                         isDirectContact,
                                         watch,
                                         questionsStepHistory,
                                         currentStep
) => {

  // This will save current form state into local storage
  if (allChoices.categoryId && isValid && !isDirectContact) {
    const questions = watch()?.questions;
    const sanitizedQuestions = {};

    // Remove empty arrays. Saved object for react-hook-form has a bug and doesnt initialize properly if { question: [] }
    Object.keys(questions)
      .forEach(key => {
        const question =  questions[key];
        if (Array.isArray(question) && question.length < 1) {
          return;
        }

        sanitizedQuestions[key] = question;
    });

    const jobForm = {
      [allChoices.categoryId]: {
        form: { questions: sanitizedQuestions },
        questionsStepHistory,
        currentStep,
        location: watch().location,
        address: watch().address
      }
    };

    if (!isSubmitted) { // Don't save submitted form
      const existingJobForm = Boolean(localStorage.getItem('job-form'))
        ? JSON.parse(localStorage.getItem('job-form'))
        : {};
      localStorage.setItem('job-form', JSON.stringify({ ...existingJobForm, ...jobForm }));
    }
  }
};

export const backButtonEffect = (
  questionsStepHistory,
  currentStep,
  prevStep,
  setBackButtonEnabled
) => {
  const hasQuestionHistory = questionsStepHistory?.length > 0;
  const isQuestionWithBack = currentStep === PostJobSteps.QUESTIONS_STEP && hasQuestionHistory;

  switch (true) {
    case (currentStep !== PostJobSteps.CATEGORY_STEP && currentStep === PostJobSteps.QUESTIONS_STEP && hasQuestionHistory)
    || prevStep.length > 0:
      setBackButtonEnabled(true);
      break;
    default:
    case currentStep === PostJobSteps.CATEGORY_STEP && !isQuestionWithBack:
      setBackButtonEnabled(false);
      break;
  }
};

export const fetchSellerEffect = async (
  profile,
  dispatch,
  isValid,
  proUsername,
  postJobSettings,
  proId,
  searchParams,
  setPrevStep,
  setValue,
  formValues,
  goToStep
) => {
  let sellerProfileCategories = profile?.relationships.autodealCategories.data;

  if (!sellerProfileCategories) {
    const sellerProfile = await dispatch(
      fetchSeller({ username: proUsername, includes: PROFILE_FETCH_COMMON_INCLUDES })
    )
      .unwrap();
    sellerProfileCategories = sellerProfile.sellerProfiles[proId].relationships.autodealCategories.data;
  }

  const proAvailableCategoryIds = sellerProfileCategories.map(cat => cat.id);
  const categoryFromUrl = searchParams.get('category');

  // In case Pro has only 1 category, navigate to it
  if (sellerProfileCategories.length === 1) {
    setValue('categoryId', sellerProfileCategories[0].id);
    goToStep(PostJobSteps.QUESTIONS_STEP);

    return;
  }

  if (!proAvailableCategoryIds.includes(categoryFromUrl)) { // In Pro Profile
    goToStep(PostJobSteps.CATEGORY_STEP);

    return;
  }

  // In case Pro has more than 1 category, let him select the category to proceed
  goToStep(PostJobSteps.CATEGORY_STEP);
};

// Used to jump into specific steps of a single bid flow
const goToSingleBidFlow = async (setPrevStep, goToStep, formValues) => {
  // Check if all data is valid before moving to these steps (isValid ?)
  const isStepValid = Object.keys(formValues.questions).length > 1
    && Boolean(formValues.location)
    && Boolean(formValues.address);

  setPrevStep(isStepValid ? [ PostJobSteps.QUESTIONS_STEP, PostJobSteps.LOCATION_STEP ] : []);
  goToStep(isStepValid ? PostJobSteps.USER_STEP : PostJobSteps.QUESTIONS_STEP);
};

export const subscribeToQuestionChangesEffect = (watch, stepChangeCallback) => {
  let prev;
  const sub = watch(values => {
    const current = JSON.stringify(values.questions);
    if (current !== prev) {
      prev = current;
      stepChangeCallback(values.questions);
    }
  });

  return () => sub.unsubscribe();
};

export const updateProgressBarEffect = (progressBarUpdate, progressTranslateX) => {
  if (typeof progressBarUpdate === 'function' && progressTranslateX !== undefined) {
    progressBarUpdate({
      progressTranslateX
    });
  }
};

export const handleReferrerEffect = (setValue, searchParams) => {
  if (!searchParams?.has('referrer')) {
    return;
  }

  const raw = searchParams.get('referrer')
    ?.trim();

  // Enforce max length
  if (!raw || raw.length > 50) {
    return;
  }

  // Ensure it’s numeric only before parsing
  if (!/^-?\d+$/.test(raw)) {
    return;
  }

  const refId = parseInt(raw, 0);

  if (!Number.isNaN(refId)) {
    setValue('referrerId', refId);
  }
};

// Runs the server geo-check for the given request: reports the analytics payload,
// flags an invalid area, records the served address, and writes the resolved
// county/municipality/location back into the form. Returns whether the area is served.
// `context` carries the location step's form setters and props (see checkLocationEffect).
export const geoCheckEffect = async (geoCheckParams, context) => {
  const {
    apiUrl,
    validAddresses,
    setValidAddresses,
    setValue,
    setError,
    clearErrors,
    trigger,
    setUserGis,
    onResultChecked,
    translate
  } = context;

  const geoCheck = await axios.get<GeoCheckCategoryResponse>(`${apiUrl}api/public/geo_check_category/`, geoCheckParams);
  const geoCheckResponse = geoCheck.data;

  if (typeof onResultChecked === 'function') {
    onResultChecked({
      job_latitude: geoCheckParams.params.query.latitude,
      job_longitude: geoCheckParams.params.query.longitude,
      county_slug: geoCheckResponse.county_slug,
      location_query: geoCheckParams.params.query.description,
      is_location_served_by_us: geoCheckResponse.is_valid
    });
  }

  if (!geoCheckResponse.is_valid) {
    setError('server.location', {
      type: 'server',
      message: translate('T.VALIDATOR.Location is invalid')
    });
  } else {
    if (!validAddresses.includes(geoCheckParams.params.location)) {
      validAddresses.push(geoCheckParams.params.location);
    }
    setValidAddresses(validAddresses);
    clearErrors('server.location');
  }

  setValue('county', geoCheckResponse.county_slug);
  setValue('municipality', geoCheckResponse.municipality_slug);
  setValue('location', geoCheckParams.params.location);
  setUserGis(geoCheckParams.params.query);
  trigger(); // Trigger the form to pass values and trigger validations

  return geoCheckResponse.is_valid;
};

// Builds the geo-check request for a location picked via autocomplete or the map
// (adding the pro scope when a pro is targeted and individual geo-check is off),
// then runs it. Returns whether the area is served.
export const checkLocationEffect = async (location: Gis, context) => {
  if (location.longitude === undefined) {
    return;
  }

  const point = `POINT(${location.longitude} ${location.latitude})`;
  const geoCheckParams = {
    params: {
      format: 'json',
      category_id: context.categoryId,
      query: location,
      location: point,
      pro_id: undefined
    }
  };

  // If a pro exists, add pro_id to the request params
  if (context.proId && !context.individualGeoCheck) {
    geoCheckParams.params.pro_id = context.proId;
  }

  return geoCheckEffect(geoCheckParams, context);
};

// On mount, re-run the geo-check when the form already carries a location + address
// (e.g. restored from local storage) so the served-area validation is re-established.
export const initialGeoCheckEffect = (watch, context) => {
  const currentValues = watch();

  if (currentValues.location !== undefined
      && currentValues.location !== ''
      && currentValues.address !== undefined
      && currentValues.address !== ''
  ) {
    const geoCheckParams = {
      params: {
        format: 'json',
        category_id: context.categoryId,
        query: currentValues.address,
        location: currentValues.location
      }
    };

    geoCheckEffect(geoCheckParams, context);
  }
};
